import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const addDays = (iso, n) => {
    const d = new Date(iso);
    d.setDate(d.getDate() + n);
    return toDateInput(d);
};

/** 두 날 사이의 일수. 전 계약이 몇 년짜리였는지 재는 데 쓴다. */
const daysBetween = (from, to) =>
    Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000);

const emptyForm = () => ({
    tenantId: '',
    unitIds: [],
    startDate: '',
    endDate: '',
    terminatedAt: '',
    deposit: '',
    monthlyRent: '',
    monthlyFee: '',
    vatIncluded: false,
    paymentDay: '',
    memo: '',
});

/**
 * 계약 하나를 만들고 고치는 화면.
 *
 * 호실은 여러 곳을 고를 수 있다. 2·3층을 통째로 빌리는 임차인이 있고, 그때
 * 계약을 층마다 쪼개 두면 만료일이 따로 놀아 하나를 놓친다.
 */
const LeaseFormPage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    // 어느 계약을 물려받아 새로 쓰는가. 재계약은 임차인도 호실도 금액도 대개
    // 그대로이고 기간만 새로 잡는다.
    const renewFrom = searchParams.get('from');
    const navigate = useNavigate();
    const { showAlert, showConfirm } = useModal();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(emptyForm());
    const [tenants, setTenants] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newTenant, setNewTenant] = useState('');
    /** 물려받은 계약. 무엇을 이어 쓰는지 화면에 밝혀 둔다. */
    const [renewedFrom, setRenewedFrom] = useState(null);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [t, f, u] = await Promise.all([
                api.get('/tenants'),
                api.get('/facilities'),
                api.get('/units'),
            ]);
            setTenants(t.data || []);
            setFacilities(f.data || []);
            setUnits(u.data || []);

            if (isEdit || renewFrom) {
                const { data } = await api.get(`/leases/${id ?? renewFrom}`);
                const carried = {
                    tenantId: String(data.tenant.id),
                    unitIds: (data.units || []).map((x) => x.id),
                    deposit: data.deposit ?? '',
                    monthlyRent: data.monthlyRent ?? '',
                    monthlyFee: data.monthlyFee ?? '',
                    vatIncluded: Boolean(data.vatIncluded),
                    paymentDay: data.paymentDay ?? '',
                };

                if (isEdit) {
                    setForm({
                        ...carried,
                        startDate: toDateInput(data.startDate),
                        endDate: toDateInput(data.endDate),
                        terminatedAt: toDateInput(data.terminatedAt),
                        memo: data.memo ?? '',
                    });
                } else {
                    // 새 기간은 옛 계약이 끝난 다음 날부터, 길이는 그대로.
                    // 2년을 쓰던 임차인은 대개 다시 2년을 쓴다. 틀리면 고치면
                    // 되지만, 빈칸으로 두면 매번 달력을 두 번 열어야 한다.
                    const start = addDays(data.endDate, 1);
                    const span = daysBetween(data.startDate, data.endDate);
                    setForm({
                        ...carried,
                        startDate: start,
                        endDate: addDays(start, span),
                        terminatedAt: '',
                        // 메모는 물려받지 않는다. 지난 계약에 대한 말이라 새
                        // 계약에 그대로 붙으면 사실이 아닌 것이 남는다.
                        memo: '',
                    });
                    setRenewedFrom(data);
                }
            }
        } catch (err) {
            console.error('Failed to load lease form', err);
            showAlert('오류', '불러오지 못했습니다.', 'danger');
        } finally {
            setLoading(false);
        }
    }, [id, isEdit, renewFrom, showAlert]);

    useEffect(() => {
        load();
    }, [load]);

    const toggleUnit = (unitId) =>
        set({
            unitIds: form.unitIds.includes(unitId)
                ? form.unitIds.filter((x) => x !== unitId)
                : [...form.unitIds, unitId],
        });

    /** 임차인을 여기서 바로 만든다. 계약을 쓰다 말고 다른 화면에 다녀오게 하면
        적던 내용이 날아간다. */
    const addTenant = async () => {
        const name = newTenant.trim();
        if (!name) return;
        try {
            const { data } = await api.post('/tenants', { name });
            setTenants((prev) => [...prev, data]);
            set({ tenantId: String(data.id) });
            setNewTenant('');
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '만들지 못했습니다.', 'danger');
        }
    };

    const save = async () => {
        if (!form.tenantId) {
            showAlert('알림', '임차인을 골라 주세요.', 'warning');
            return;
        }
        if (form.unitIds.length === 0) {
            showAlert('알림', '빌리는 호실을 하나 이상 골라 주세요.', 'warning');
            return;
        }
        if (!form.startDate || !form.endDate) {
            showAlert('알림', '계약 기간을 적어 주세요.', 'warning');
            return;
        }

        setSaving(true);
        try {
            const body = {
                tenantId: Number(form.tenantId),
                unitIds: form.unitIds,
                startDate: form.startDate,
                endDate: form.endDate,
                terminatedAt: form.terminatedAt || null,
                deposit: form.deposit === '' ? null : Number(form.deposit),
                monthlyRent: form.monthlyRent === '' ? null : Number(form.monthlyRent),
                monthlyFee: form.monthlyFee === '' ? null : Number(form.monthlyFee),
                vatIncluded: form.vatIncluded,
                paymentDay: form.paymentDay === '' ? null : Number(form.paymentDay),
                memo: form.memo,
            };
            if (isEdit) await api.patch(`/leases/${id}`, body);
            else await api.post('/leases', body);
            navigate('/leases');
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '저장하지 못했습니다.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const ok = await showConfirm(
            '계약 삭제',
            '이 계약을 지울까요? 끝난 계약은 지우지 말고 두는 편이 낫습니다 — 이 호실에 누가 있었는지가 사라집니다.',
            'danger',
        );
        if (!ok) return;
        try {
            await api.delete(`/leases/${id}`);
            navigate('/leases');
        } catch (err) {
            showAlert('오류', '지우지 못했습니다.', 'danger');
        }
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="pb-5">
            <div className="d-flex align-items-center gap-2 mb-3">
                <button className="btn border-0 p-0" onClick={() => navigate(-1)}>
                    <i className="bi bi-chevron-left fs-5"></i>
                </button>
                <h4 className="fw-bold mb-0">
                    {isEdit ? '계약 수정' : renewFrom ? '재계약' : '계약 등록'}
                </h4>
            </div>

            {/* 무엇을 물려받았는지 밝혀 둔다. 값이 미리 채워져 있는데 어디서 온
                것인지 모르면, 맞는지 확인할 방법이 없다. */}
            {renewedFrom && (
                <div className="bg-white rounded-4 border p-3 mb-3">
                    <div className="small">
                        <i className="bi bi-arrow-repeat text-primary me-2"></i>
                        <strong>{renewedFrom.tenant?.name}</strong>의 지난 계약을 이어 씁니다.
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                        {toDateInput(renewedFrom.startDate)} ~ {toDateInput(renewedFrom.endDate)} ·
                        호실과 금액을 그대로 가져왔습니다. 바뀐 것만 고쳐 주세요.
                    </div>
                </div>
            )}

            <div className="bg-white rounded-4 border p-3 mb-3">
                <label className="form-label fw-bold small">임차인</label>
                <select
                    className="form-select mb-2"
                    value={form.tenantId}
                    onChange={(e) => set({ tenantId: e.target.value })}
                >
                    <option value="">고르세요</option>
                    {tenants.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
                <div className="d-flex gap-2">
                    <input
                        className="form-control"
                        placeholder="새 임차인 상호"
                        value={newTenant}
                        onChange={(e) => setNewTenant(e.target.value)}
                    />
                    <button className="btn btn-white border text-nowrap" onClick={addTenant}>
                        추가
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-4 border p-3 mb-3">
                <label className="form-label fw-bold small">빌리는 호실</label>
                {units.length === 0 ? (
                    <div className="text-muted small">
                        빌려줄 곳이 아직 없습니다. 시설 상세의 「호실 관리」에서 먼저 더해 주세요.
                    </div>
                ) : (
                    facilities.map((f) => {
                        const own = units.filter((u) => u.facility?.id === f.id);
                        if (own.length === 0) return null;
                        return (
                            <div key={f.id} className="mb-2">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{f.name}</div>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    {own.map((u) => {
                                        const picked = form.unitIds.includes(u.id);
                                        // 이미 다른 계약이 쓰는 호실도 고를 수는 있다. 재계약을
                                        // 미리 넣어 두는 일이 있어서 막지 않고, 대신 알려 준다.
                                        const busy = u.currentLease && !picked;
                                        return (
                                            <button
                                                key={u.id}
                                                className={`btn btn-sm rounded-pill px-3 ${picked ? 'btn-primary' : 'btn-white border'}`}
                                                onClick={() => toggleUnit(u.id)}
                                            >
                                                {u.name}
                                                {busy && (
                                                    <span className="ms-1 text-warning" title={`${u.currentLease.tenantName} 사용 중`}>
                                                        ●
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}
                <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                    ● 은 다른 계약이 쓰는 중인 호실입니다. 재계약을 미리 넣을 수 있게 막지는 않습니다.
                </div>
            </div>

            <div className="bg-white rounded-4 border p-3 mb-3">
                <label className="form-label fw-bold small">계약 기간</label>
                <div className="row g-2">
                    <div className="col-6">
                        <input
                            type="date"
                            className="form-control"
                            value={form.startDate}
                            onChange={(e) => set({ startDate: e.target.value })}
                        />
                    </div>
                    <div className="col-6">
                        <input
                            type="date"
                            className="form-control"
                            value={form.endDate}
                            onChange={(e) => set({ endDate: e.target.value })}
                        />
                    </div>
                </div>
                <label className="form-label fw-bold small mt-3">중도 해지일</label>
                <input
                    type="date"
                    className="form-control"
                    value={form.terminatedAt}
                    onChange={(e) => set({ terminatedAt: e.target.value })}
                />
                <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                    비워 두세요. 계약이 기간을 채우지 못하고 끝났을 때만 적습니다.
                </div>
            </div>

            <div className="bg-white rounded-4 border p-3 mb-3">
                <label className="form-label fw-bold small">금액</label>
                <div className="row g-2">
                    <div className="col-12 col-md-4">
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>보증금</div>
                        <input
                            type="number"
                            className="form-control"
                            value={form.deposit}
                            onChange={(e) => set({ deposit: e.target.value })}
                        />
                    </div>
                    <div className="col-6 col-md-4">
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>월 임대료</div>
                        <input
                            type="number"
                            className="form-control"
                            value={form.monthlyRent}
                            onChange={(e) => set({ monthlyRent: e.target.value })}
                        />
                    </div>
                    <div className="col-6 col-md-4">
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>월 관리비</div>
                        <input
                            type="number"
                            className="form-control"
                            value={form.monthlyFee}
                            onChange={(e) => set({ monthlyFee: e.target.value })}
                        />
                    </div>
                    <div className="col-6 col-md-4">
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>납부일</div>
                        <input
                            type="number"
                            min="1"
                            max="31"
                            className="form-control"
                            value={form.paymentDay}
                            onChange={(e) => set({ paymentDay: e.target.value })}
                        />
                    </div>
                    <div className="col-6 col-md-4 d-flex align-items-end pb-2">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="vat"
                                checked={form.vatIncluded}
                                onChange={(e) => set({ vatIncluded: e.target.checked })}
                            />
                            <label className="form-check-label small" htmlFor="vat">
                                부가세 포함 금액
                            </label>
                        </div>
                    </div>
                </div>
                {/* 청구서를 만들지 않는다는 것을 분명히 해 둔다. 적어 두면 매달
                    자동으로 청구되는 줄 알기 쉽다. */}
                <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                    적어 두기만 합니다. 매달 청구나 입금 관리는 아직 없습니다.
                </div>
            </div>

            <div className="bg-white rounded-4 border p-3 mb-3">
                <label className="form-label fw-bold small">메모</label>
                <textarea
                    className="form-control"
                    rows="2"
                    value={form.memo}
                    onChange={(e) => set({ memo: e.target.value })}
                />
            </div>

            <div className="d-flex gap-2">
                {isEdit && (
                    <button className="btn btn-white border text-danger" onClick={remove}>
                        삭제
                    </button>
                )}
                {isEdit && (
                    <button
                        className="btn btn-white border text-nowrap"
                        onClick={() => navigate(`/leases/new?from=${id}`)}
                    >
                        재계약
                    </button>
                )}
                <button
                    className="btn btn-primary flex-grow-1 fw-bold"
                    onClick={save}
                    disabled={saving}
                >
                    {saving ? '저장중…' : '저장'}
                </button>
            </div>
        </div>
    );
};

export default LeaseFormPage;
